import { oc } from '@orpc/contract';
import { z } from 'zod';
import { FieldValues } from './catalog.js';
import { EventInput, StorageKind, ValuationInput } from './collections.js';
import { Timestamp } from './common.js';
// ---------- формат JSON-выгрузки (docs/PLAN.md, этап 2) ----------
const Ref = z.string();
/** Справочное значение, на которое ссылаются поля: по нему импорт находит запись на другом сервере. */
export const ExportRef = z.object({
    kind: z.string(),
    code: z.string().nullable(),
    name: z.string(),
});
export const ExportCatalogItem = z.object({
    id: Ref,
    /** Общая запись каталога (есть на сервере) или своя приватная. */
    shared: z.boolean(),
    typeCode: z.string(),
    numistaId: z.number().int().nullable(),
    values: FieldValues,
    variants: z.array(z.object({ id: Ref, label: z.string(), values: FieldValues })),
});
export const ExportSpecimen = z.object({
    id: Ref,
    catalogItemId: Ref,
    variantId: Ref.nullable(),
    /** Собственные значения экземпляра (коды полей из метаданных). */
    values: FieldValues,
    tagIds: z.array(Ref),
    events: z.array(EventInput),
    valuations: z.array(ValuationInput),
    notes: z.array(z.object({ bodyMd: z.string(), createdAt: Timestamp })),
});
export const ExportFile = z.object({
    format: z.literal('numistic-export'),
    version: z.literal(1),
    exportedAt: Timestamp,
    baseCurrency: z.string(),
    collections: z.array(z.object({
        id: Ref,
        parentId: Ref.nullable(),
        name: z.string(),
        description: z.string().nullable(),
        isInbox: z.boolean(),
    })),
    locations: z.array(z.object({ id: Ref, parentId: Ref.nullable(), name: z.string(), kind: StorageKind })),
    tags: z.array(z.object({ id: Ref, name: z.string(), color: z.string().nullable() })),
    refs: z.record(z.string(), ExportRef),
    catalogItems: z.array(ExportCatalogItem),
    specimens: z.array(ExportSpecimen),
});
// ---------- импорт ----------
export const ImportIssue = z.object({
    /** Номер строки CSV (с 2 — после заголовка) или индекс экземпляра в JSON; null — файл целиком. */
    row: z.number().int().nullable(),
    column: z.string().nullable(),
    message: z.string(),
});
export const ImportReport = z.object({
    dryRun: z.boolean(),
    rows: z.number().int(),
    /** Импортировано (или будет импортировано при dry-run) экземпляров. */
    imported: z.number().int(),
    skipped: z.number().int(),
    created: z.object({
        collections: z.number().int(),
        locations: z.number().int(),
        tags: z.number().int(),
        catalogItems: z.number().int(),
        events: z.number().int(),
        valuations: z.number().int(),
        notes: z.number().int(),
    }),
    issues: z.array(ImportIssue),
});
export const CsvTarget = z.object({ code: z.string(), label: z.string() });
export const CsvPreview = z.object({
    headers: z.array(z.string()),
    /** Предлагаемое сопоставление: заголовок → код цели (или null — не импортировать). */
    suggested: z.record(z.string(), z.string().nullable()),
    targets: z.array(CsvTarget),
    sample: z.array(z.array(z.string())),
    rows: z.number().int(),
});
const ioErrors = {
    INVALID_FILE: { status: 400, message: 'invalid_file' },
    FILE_TOO_LARGE: { status: 413, message: 'file_too_large' },
};
export const MAX_IMPORT_BYTES = 25 * 1024 * 1024;
export const ioContract = {
    /** Заголовки и первые строки CSV с предложенным сопоставлением колонок. */
    csvColumns: oc
        .route({ method: 'POST', path: '/import/csv/columns' })
        .errors(ioErrors)
        .input(z.object({ file: z.file() }))
        .output(CsvPreview),
    importCsv: oc
        .route({ method: 'POST', path: '/import/csv' })
        .errors(ioErrors)
        .input(z.object({
        file: z.file(),
        /** JSON: { заголовок: код цели }. */
        mapping: z.string().max(100_000),
        dryRun: z.stringbool(),
    }))
        .output(ImportReport),
    importJson: oc
        .route({ method: 'POST', path: '/import/json' })
        .errors(ioErrors)
        .input(z.object({ file: z.file(), dryRun: z.stringbool() }))
        .output(ImportReport),
};
//# sourceMappingURL=io.js.map